import { Vector } from '@ardoq/graph';
import { BlocksViewNode, EditPreview, LayoutType, UndoStack } from '../types';
import { isGroup } from '../viewModel$/util';
import { resetLayout, setNodeLayoutData } from './layout';
import { getNodeLayoutData, stripGrid } from './util';

type Entry = UndoStack[number];

type EntryItem = Entry[number];

const getEntryItem = (node: BlocksViewNode): EntryItem => {
  return {
    node: node,
    ...getNodeLayoutData(node),
  };
};

export const editCanUndo = (stack: UndoStack) => {
  return stack.length > 0;
};

export const editUndo = (stack: UndoStack) => {
  const entry = stack.pop();

  const setLayout = (
    u: BlocksViewNode,
    col: number,
    row: number,
    colSpan: number,
    rowSpan: number
  ) => {
    u.col = col;
    u.row = row;
    u.colSpan = colSpan;
    u.rowSpan = rowSpan;
  };

  if (entry) {
    const groups = new Set<BlocksViewNode>();

    for (const entryItem of entry) {
      const node = entryItem.node;

      if (node.parent) {
        groups.add(node.parent);
      }

      setNodeLayoutData(entryItem.node, entryItem);
    }

    for (const group of groups) {
      stripGrid(group, setLayout);
    }
  }
};

export const editReset = (
  stack: UndoStack,
  groups: Iterable<BlocksViewNode>,
  type: LayoutType
) => {
  const entry: Entry = [];

  for (const group of groups) {
    if (isGroup(group)) {
      for (const child of group.children!) {
        entry.push(getEntryItem(child));
      }

      resetLayout(group, type);
    }
  }

  if (entry.length !== 0) {
    stack.push(entry);
  }
};

export const editApply = (stack: UndoStack, editPreview: EditPreview) => {
  const node = editPreview.node;
  const group = node.parent;
  const siblings = group && isGroup(group) ? group.children : null;

  if (!group || !siblings || !editPreview.isGood) {
    return;
  }

  const dirtyNodes = new Map<BlocksViewNode, EntryItem>();

  const setLayout = (
    u: BlocksViewNode,
    col: number,
    row: number,
    colSpan: number,
    rowSpan: number
  ) => {
    if (!dirtyNodes.has(u)) {
      dirtyNodes.set(u, getEntryItem(u));
    }

    u.col = col;
    u.row = row;
    u.colSpan = colSpan;
    u.rowSpan = rowSpan;
  };

  /* linked groups share tracks, so an inserted track moves their blocks too */
  const partnerChildren = (axis: 'col' | 'row'): BlocksViewNode[] =>
    (group.alignedWith?.[axis] ?? []).flatMap(
      partner => partner.children ?? []
    );
  const colMates = [...siblings, ...partnerChildren('col')];
  const rowMates = [...siblings, ...partnerChildren('row')];

  const shuffleColumn = (col: number) => {
    for (const u of colMates) {
      if (u !== node && u.col <= col && u.col + u.colSpan > col) {
        setLayout(u, u.col, u.row, u.colSpan + 2, u.rowSpan);
        continue;
      }

      if (u !== node && u.col > col) {
        setLayout(u, u.col + 2, u.row, u.colSpan, u.rowSpan);
        continue;
      }
    }
  };

  const shuffleRow = (row: number) => {
    for (const u of rowMates) {
      if (u !== node && u.row <= row && u.row + u.rowSpan > row) {
        setLayout(u, u.col, u.row, u.colSpan, u.rowSpan + 2);
        continue;
      }

      if (u !== node && u.row > row) {
        setLayout(u, u.col, u.row + 2, u.colSpan, u.rowSpan);
        continue;
      }
    }
  };

  /* insert rows and columns */

  if (!isNaN(editPreview.insert[0])) {
    shuffleColumn(editPreview.insert[0]);
  }

  if (!isNaN(editPreview.insert[1])) {
    shuffleRow(editPreview.insert[1]);
  }

  /* set the node position and span */

  setLayout(
    node,
    editPreview.posn[0],
    editPreview.posn[1],
    editPreview.span[0],
    editPreview.span[1]
  );

  /*  strip dead columns and rows */

  stripGrid(group, setLayout);

  /* build an undo stack entry for any dirty nodes */

  const entry: Entry = [];

  for (const entryItem of dirtyNodes.values()) {
    entry.push(entryItem);
  }

  if (entry.length !== 0) {
    stack.push(entry);
  }
};

export const editFlipHorz = (
  stack: UndoStack,
  groups: Iterable<BlocksViewNode>
) => {
  const entry: Entry = [];

  for (const group of groups) {
    if (isGroup(group) && group.colSize && group.rowSize) {
      const colMax = group.colSize!.length - 1;

      for (const child of group.children) {
        const col = colMax - (child.col + child.colSpan - 1);

        if (col !== child.col) {
          entry.push(getEntryItem(child));
          child.col = col;
        }
      }
    }
  }

  if (entry.length !== 0) {
    stack.push(entry);
  }
};

export const editFlipVert = (
  stack: UndoStack,
  groups: Iterable<BlocksViewNode>
) => {
  const entry: Entry = [];

  for (const group of groups) {
    if (isGroup(group) && group.colSize && group.rowSize) {
      const rowMax = group.rowSize!.length - 1;

      for (const child of group.children) {
        const row = rowMax - (child.row + child.rowSpan - 1);

        if (child.row !== row) {
          entry.push(getEntryItem(child));
          child.row = row;
        }
      }
    }
  }

  if (entry.length !== 0) {
    stack.push(entry);
  }
};

export const editRotate = (
  stack: UndoStack,
  groups: Iterable<BlocksViewNode>
) => {
  const entry: Entry = [];

  for (const group of groups) {
    if (isGroup(group)) {
      /* rotate the children */

      const colMax = group.colSize!.length - 1;

      for (const child of group.children) {
        const left = child.row;
        const top = colMax - (child.col + child.colSpan - 1);

        if (
          child.col !== left ||
          child.row !== top ||
          child.colSpan !== child.rowSpan
        ) {
          entry.push(getEntryItem(child));

          child.col = left;
          child.row = top;

          const t = child.colSpan;
          child.colSpan = child.rowSpan;
          child.rowSpan = t;
        }
      }

      /* rotate the group */

      const colSize = group.colSize;
      group.colSize = group.rowSize;
      group.rowSize = colSize;
    }
  }

  if (entry.length !== 0) {
    stack.push(entry);
  }
};

/* scaffolding for old edit interactions; to be removed */
export const editResize = (
  stack: UndoStack,
  node: BlocksViewNode,
  col: number,
  colSpan: number,
  row: number,
  rowSpan: number
) => {
  if (
    node.col !== col ||
    node.colSpan !== colSpan ||
    node.row !== row ||
    node.rowSpan !== rowSpan
  ) {
    const entry: Entry = [];
    const shuffle = [0, 0];
    const group = node.parent!;

    if (!isGroup(group)) {
      return;
    }

    if (col < 1 || row < 1) {
      shuffle[0] = col < 1 ? -col + 1 : 0; // make sure that nodes start on col 1
      shuffle[1] = row < 1 ? -row + 1 : 0; // make sure that nodes start on row 1

      const group = node.parent!;

      for (const child of group.children!) {
        entry.push(getEntryItem(child));

        child.col += shuffle[0];
        child.row += shuffle[1];
      }
    } else {
      entry.push(getEntryItem(node));
    }

    node.col = col + shuffle[0];
    node.colSpan = colSpan;
    node.row = row + shuffle[1];
    node.rowSpan = rowSpan;

    stack.push(entry);
  }
};
/* scaffolding for old edit interactions; to be removed */
export const editMove = (
  stack: UndoStack,
  group: BlocksViewNode,
  nodes: Iterable<BlocksViewNode>,
  dragOffset: Vector
) => {
  if (!isGroup(group)) {
    return;
  }

  if (dragOffset[0] === 0 && dragOffset[1] === 0) {
    return;
  }

  const entry: Entry = [];
  const shuffle = [0, 0];

  if (dragOffset[0] < 0 || dragOffset[1] < 0) {
    let colMin = +Infinity;
    let rowMin = +Infinity;

    for (const node of nodes) {
      colMin = Math.min(colMin, node.col + dragOffset[0]);
      rowMin = Math.min(rowMin, node.row + dragOffset[1]);
    }

    shuffle[0] = colMin < 1 ? -colMin + 1 : 0;
    shuffle[1] = rowMin < 1 ? -rowMin + 1 : 0;
  }

  if (shuffle[0] !== 0 || shuffle[1] !== 0) {
    for (const child of group.children!) {
      entry.push(getEntryItem(child));

      child.col += shuffle[0];
      child.row += shuffle[1];
    }
  } else {
    for (const node of nodes) {
      entry.push(getEntryItem(node));
    }
  }

  for (const node of nodes) {
    node.col += dragOffset[0];
    node.row += dragOffset[1];
  }

  if (entry.length !== 0) {
    stack.push(entry);
  }
};
/* scaffolding for old edit interactions; to be removed */
export const editInsertCol = (
  stack: UndoStack,
  group: BlocksViewNode,
  col: number
) => {
  if (!group.children?.length) {
    return;
  }

  const entry: Entry = [];

  for (const child of group.children) {
    if (child.col > col) {
      entry.push(getEntryItem(child));

      child.col += 2;
    }
  }

  if (entry.length !== 0) {
    stack.push(entry);
  }
};
/* scaffolding for old edit interactions; to be removed */
export const editCanDeleteCol = (group: BlocksViewNode, col: number) => {
  if (!group.children?.length) {
    return;
  }

  if (!group.colSize || col <= 0 || col >= group.colSize.length - 1) {
    return false;
  }

  if ((col & 1) === 0) {
    // can only delete routing col if the both adjacent node col are empty and col isn't spanned
    // the test is too sketchy to try implementing before the release

    return false;
  }

  for (const child of group.children) {
    if (child.col >= col && child.col + child.colSpan - 1 <= col) {
      return false;
    }
  }

  return true;
};
/* scaffolding for old edit interactions; to be removed */
export const editDeleteCol = (
  stack: UndoStack,
  group: BlocksViewNode,
  col: number
) => {
  if (!group.children?.length) {
    return;
  }

  const entry: Entry = [];

  for (const child of group.children) {
    if (child.col > col) {
      entry.push(getEntryItem(child));
      child.col -= 2;
    }
  }

  if (entry.length > 0) {
    stack.push(entry);
  }
};
/* scaffolding for old edit interactions; to be removed */
export const editInsertRow = (
  stack: UndoStack,
  group: BlocksViewNode,
  row: number
) => {
  if (!group.children?.length) {
    return;
  }

  const entry: Entry = [];

  for (const child of group.children) {
    if (child.row > row) {
      entry.push(getEntryItem(child));
      child.row += 2;
    }
  }

  if (entry.length > 0) {
    stack.push(entry);
  }
};
/* scaffolding for old edit interactions; to be removed */
export const editCanDeleteRow = (group: BlocksViewNode, row: number) => {
  if (!group.children?.length) {
    return false;
  }

  if (!group.rowSize || row <= 0 || row >= group.rowSize.length - 1) {
    return false;
  }

  if ((row & 1) === 0) {
    // the test is too sketchy to try implementing before the release

    return false;
  }

  for (const child of group.children) {
    if (child.row >= row && child.row + child.rowSpan - 1 <= row) {
      return false;
    }
  }

  return true;
};
/* scaffolding for old edit interactions; to be removed */
export const editDeleteRow = (
  stack: UndoStack,
  group: BlocksViewNode,
  row: number
) => {
  if (!group.children?.length) {
    return;
  }

  const entry: Entry = [];

  for (const child of group.children) {
    if (child.row > row) {
      entry.push(getEntryItem(child));
      child.row -= 2;
    }
  }

  if (entry.length > 0) {
    stack.push(entry);
  }
};
